/**
 * Color helpers for white-label theming.
 *
 * Ported from scio2 (`optisigns-portal-common/.../helpers/color.ts`) — the proven HSL-lightness
 * derivation used to build `--color-primary` variants from a customer's `colorTheme`. Kept as pure
 * functions (no DOM) so they're trivially unit-testable; the DOM application lives in
 * `features/branding/useBrandingTheme.ts`.
 */

interface Rgb {
  r: number;
  g: number;
  b: number;
}

interface Hsl {
  h: number;
  s: number;
  l: number;
}

const HEX_RE = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i;
const SHORT_OR_LONG_HEX_RE = /^#([a-f\d]{3}){1,2}$/i;

function hexToRgb(hex: string): Rgb | null {
  const result = HEX_RE.exec(hex);
  if (!result) return null;
  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  };
}

function rgbToHsl(r: number, g: number, b: number): Hsl {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return { h, s, l };
}

function hslToRgb(h: number, s: number, l: number): Rgb {
  let r: number;
  let g: number;
  let b: number;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = (p: number, q: number, t: number): number => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return { r: Math.round(r * 255), g: Math.round(g * 255), b: Math.round(b * 255) };
}

function rgbToHex(r: number, g: number, b: number): string {
  return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

/** True if `color` is a valid 3- or 6-digit hex string (e.g. `#1ab394`). */
export function isValidHexColor(color: string | undefined | null): color is string {
  return !!color && SHORT_OR_LONG_HEX_RE.test(color);
}

/**
 * Lighten/darken a hex color by scaling its HSL lightness. `factor > 1` lightens, `< 1` darkens.
 * Returns the input unchanged if it isn't a parseable 6-digit hex. (scio2 parity.)
 */
export function adjustLightness(hexColor: string, lightnessFactor: number): string {
  const rgb = hexToRgb(hexColor);
  if (!rgb) return hexColor;
  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
  hsl.l = Math.max(0, Math.min(1, hsl.l * lightnessFactor));
  const newRgb = hslToRgb(hsl.h, hsl.s, hsl.l);
  return rgbToHex(newRgb.r, newRgb.g, newRgb.b);
}

/**
 * Convert a hex color + opacity (0–1) to an `rgba(...)` string — used for the white-label login-box
 * background. Falls back to white if the hex can't be parsed.
 */
export function hexToRgba(hex: string | undefined | null, opacity: number): string {
  const rgb = (hex && hexToRgb(hex)) || { r: 255, g: 255, b: 255 };
  const a = Math.max(0, Math.min(1, Number.isFinite(opacity) ? opacity : 0));
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${a})`;
}
